import { useEffect, useState, type ReactNode } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { Pause, Play, SkipBack, SkipForward } from 'lucide-react'
import { api } from '@/runtime'
import type { Analytics, BrowseResult, LibraryItem, Listening, SpotifyDevice } from '@/db/types'
import { useNow } from '@/hooks/useNow'
import { useBrowserOnline } from '@/hooks/useBrowserOnline'
import { usePlayer } from '@/state/player'
import { Button, Empty, Failed, PageTitle, SectionTitle, cx } from '@/components/ui'
import { PlayButton } from '@/components/Player'
import { Busy, SkeletonList } from '@/components/Skeleton'
import { albumTo, artistTo, itemTo } from './Insights'
import { AppTabs, LIBRARY_TABS } from '@/components/AppTabs'

/**
 * Music is its own part of the app: what is playing now, the history's
 * insights, and the artists, albums, tracks and playlists to browse and play
 * from. Everything here is online-only (the library's tables), so the pages
 * show placeholders while the server answers, the reason and Try again when
 * it does not, and never a blank.
 */
const TABS = [
  { to: '/music', label: 'Now' },
  { to: '/music/insights', label: 'Insights' },
  { to: '/music/artists', label: 'Artists' },
  { to: '/music/albums', label: 'Albums' },
  { to: '/music/tracks', label: 'Tracks' },
  { to: '/music/playlists', label: 'Playlists' },
] as const

export const MusicNav = () => {
  const path = useRouterState({ select: (s) => s.location.pathname })
  return (
    // bleeds to the screen's edge, so a chip that does not fit scrolls off it rather than being sliced at the gutter
    <nav
      className="flex gap-1 mb-4 overflow-x-auto scrollbar-none -mx-3 px-3"
      aria-label="Music"
      data-testid="music-nav"
    >
      {TABS.map((t) => {
        const on = t.to === '/music' ? path === '/music' : path.startsWith(t.to)
        return (
          <Link
            key={t.to}
            to={t.to}
            aria-current={on ? 'page' : undefined}
            className={cx(
              'shrink-0 rounded-full px-3 min-h-8 inline-flex items-center text-xs font-medium whitespace-nowrap border',
              on
                ? 'bg-accent text-on-accent border-transparent'
                : 'bg-raised border-line text-fg-2 hover:border-line-strong',
            )}
          >
            {t.label}
          </Link>
        )
      })}
    </nav>
  )
}

/**
 * The one header of every Library page: the title, the Music / Films switch,
 * and on Music's pages the chips. Kept the same on each so nothing under it
 * jumps from tab to tab.
 */
export const LibraryHeader = ({ right, music = true }: { right?: ReactNode; music?: boolean }) => (
  <>
    <PageTitle right={right}>Library</PageTitle>
    <AppTabs tabs={LIBRARY_TABS} label="Library" />
    {music && <MusicNav />}
  </>
)

/** Artwork, or one placeholder for all of it: the note on a tile (a circle for an artist). */
export const Cover = ({
  image,
  round,
  size = 'md',
  icon = '♪',
}: {
  image?: string
  round?: boolean
  size?: 'sm' | 'md'
  icon?: string
}) => {
  const box = cx(
    'shrink-0 bg-raised',
    size === 'sm' ? 'w-9 h-9' : 'w-10 h-10',
    round ? 'rounded-full' : 'rounded-[6px]',
  )
  return image ? (
    <img src={image} alt="" className={cx(box, 'object-cover')} loading="lazy" />
  ) : (
    <span className={cx(box, 'flex items-center justify-center text-base text-faint')} aria-hidden>
      {icon}
    </span>
  )
}

/**
 * An online read: its answer, why it failed, and a way to ask again. `load` is
 * a module-level function; `key` changing asks afresh.
 */
const useRead = <T,>(key: string, load: () => Promise<T>) => {
  const [held, setHeld] = useState<{ data: T | null; error: string | null }>({ data: null, error: null })
  const [nudge, setNudge] = useState(0)
  useEffect(() => {
    let stale = false
    load()
      .then((data) => {
        if (!stale) setHeld({ data, error: null })
      })
      .catch((e: unknown) => {
        if (!stale) setHeld((h) => ({ data: h.data, error: e instanceof Error ? e.message : 'no answer' }))
      })
    return () => {
      stale = true
    }
  }, [key, load, nudge])
  return { ...held, retry: () => setNudge((n) => n + 1) }
}

const askDevices = () => api.spotifyDevices().then((r) => r.devices)
const askWeek = () => api.libraryAnalytics({ days: 7 })
const askRecent = () => api.libraryListening(7)
const askPlaylists = () => api.libraryList({ kind: 'playlist' }).then((r) => r.items)

const fmtNum = (n: number) => n.toLocaleString()
const fmtAt = (iso: string) => {
  const d = new Date(iso)
  const sameDay = d.toDateString() === new Date().toDateString()
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  return sameDay
    ? time
    : `${d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} ${time}`
}
const mmss = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** The big player: what is on, where, and the transport. */
const NowPlaying = () => {
  const state = usePlayer((s) => s.state)
  const control = usePlayer((s) => s.control)
  const watch = usePlayer((s) => s.watch)
  const retry = usePlayer((s) => s.retry)
  const lost = usePlayer((s) => s.lost)
  const error = usePlayer((s) => s.error)
  const online = useBrowserOnline()
  const now = useNow(250)
  useEffect(() => watch(), [watch])
  const devices = useRead<SpotifyDevice[]>(String(!!state?.connected), askDevices)
  // offline, what is held may be long gone, and no speaker can be reached
  if (!online) return <Failed what="what is playing" />
  if (!state)
    return lost ? <Failed what="what is playing" reason={lost} onRetry={retry} /> : <SkeletonList rows={2} />
  if (!state.connected)
    return (
      <div className="card p-4 text-sm text-muted">
        Spotify is not connected.{' '}
        <Link to="/settings/$section" params={{ section: 'spotify' }} className="underline">
          Connect it in Settings
        </Link>{' '}
        to see what is playing and play from here.
      </div>
    )
  const base = state.progress_ms + (state.playing ? Math.max(0, now - Date.parse(state.fetched_at)) : 0)
  const progress = state.duration_ms ? Math.min(1, base / state.duration_ms) : 0
  const speakers = devices.data ?? []
  return (
    <div className="card p-4" data-testid="music-now">
      {state.title ? (
        <div className="flex gap-4 items-center">
          {state.image ? (
            <img
              src={state.image}
              alt=""
              className="w-28 h-28 md:w-36 md:h-36 rounded-[var(--radius-card-inner)] object-cover shrink-0"
            />
          ) : (
            <span className="w-28 h-28 rounded-[var(--radius-card-inner)] bg-raised shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-xl font-semibold tracking-tight truncate">{state.title}</div>
            <div className="text-sm text-fg-2 truncate">{state.artist}</div>
            {state.album && <div className="text-xs text-faint truncate">{state.album}</div>}
            <div className="h-1 rounded-full bg-line mt-3 overflow-hidden">
              <div className="h-full bg-accent" style={{ width: `${progress * 100}%` }} />
            </div>
            <div className="flex justify-between text-[11px] text-faint tabular-nums mt-1">
              <span>{mmss(base)}</span>
              <span>{mmss(state.duration_ms)}</span>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button onClick={() => void control('previous').catch(() => {})} aria-label="Previous">
                <SkipBack size={16} />
              </Button>
              <Button
                variant="primary"
                onClick={() => void control(state.playing ? 'pause' : 'play').catch(() => {})}
                aria-label={state.playing ? 'Pause' : 'Play'}
                data-testid="music-toggle"
              >
                {state.playing ? <Pause size={18} /> : <Play size={18} />}
              </Button>
              <Button onClick={() => void control('next').catch(() => {})} aria-label="Next">
                <SkipForward size={16} />
              </Button>
            </div>
            {/* where it plays: the Play on row says it once the speakers are known */}
            {state.device && speakers.length === 0 && (
              <div className="text-xs text-faint mt-2 truncate">Playing on {state.device}</div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-sm text-muted">
          Nothing playing. Start something on any device, or press play on a track below.
        </div>
      )}
      {speakers.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-line" data-testid="music-devices">
          <span className="text-xs text-faint self-center mr-1">Play on</span>
          {speakers.map((d) => (
            <Button
              key={d.id}
              variant={d.active ? 'chipOn' : 'chip'}
              onClick={() => void control('play', { device_id: d.id }).catch(() => {})}
            >
              {d.name}
            </Button>
          ))}
        </div>
      ) : (
        devices.error && (
          <div className="mt-3 pt-3 border-t border-line flex">
            <Failed compact what="the speakers to play on" reason={devices.error} onRetry={devices.retry} />
          </div>
        )
      )}
      {error && (
        <p className="text-xs text-fg-2 mt-2" role="status">
          {error}
        </p>
      )}
    </div>
  )
}

/** The week's few facts as one surface, a hairline between each. */
const Highlights = ({ a }: { a: Analytics }) => (
  <ul className="group-list">
    {a.highlights.slice(0, 4).map((h, i) => (
      <li key={`${h.kind}-${i}`} className="px-3.5 py-3">
        <span className="block text-[15px] font-semibold text-accent-text leading-snug">
          {h.artist ? (
            <Link {...artistTo(h.artist)}>{h.lead}</Link>
          ) : h.item_id ? (
            <Link {...itemTo(h.item_id)}>{h.lead}</Link>
          ) : (
            h.lead
          )}
        </span>
        <span className="block text-xs text-fg-2 mt-0.5">{h.text}</span>
      </li>
    ))}
  </ul>
)

export const MusicView = () => {
  const week = useRead<Analytics>('week', askWeek)
  const recent = useRead<Listening>('recent', askRecent)
  return (
    <div data-testid="music">
      <LibraryHeader />
      <NowPlaying />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        <div className="min-w-0">
          <SectionTitle
            right={
              <Link
                to="/music/insights"
                search={{ days: 7 } as never}
                className="text-xs underline text-muted"
              >
                This week in full
              </Link>
            }
          >
            This week
          </SectionTitle>
          {week.data ? (
            week.data.highlights.length ? (
              <Highlights a={week.data} />
            ) : (
              <div className="card p-3 text-sm text-faint">Nothing played this week.</div>
            )
          ) : week.error ? (
            <Failed what="this week's listening" reason={week.error} onRetry={week.retry} />
          ) : (
            <SkeletonList rows={3} />
          )}
        </div>
        <div className="min-w-0">
          <SectionTitle>Recent</SectionTitle>
          {recent.data ? (
            <ul className="group-list" data-testid="music-recent">
              {recent.data.recent.slice(0, 12).map((r, i) => (
                <li key={`${r.id}-${i}`}>
                  <Link {...itemTo(r.id)} className="flex items-center gap-3 px-3 py-2 hover:bg-raised/60">
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm truncate">{r.title}</span>
                      <span className="block text-xs text-faint truncate">{String(r.data.artist ?? '')}</span>
                    </span>
                    <span className="text-xs text-faint shrink-0">{fmtAt(r.at)}</span>
                    <PlayButton
                      small
                      play={{ item: r.id }}
                      title={r.title}
                      uri={typeof r.data.uri === 'string' ? r.data.uri : undefined}
                    />
                  </Link>
                </li>
              ))}
              {recent.data.recent.length === 0 && (
                <li className="px-3 py-2 text-sm text-faint">
                  Nothing yet. Connect Spotify or import a history in Settings.
                </li>
              )}
            </ul>
          ) : recent.error ? (
            <Failed what="what you played lately" reason={recent.error} onRetry={recent.retry} />
          ) : (
            <SkeletonList rows={6} />
          )}
        </div>
      </div>
    </div>
  )
}

const NOUN = { artists: 'your artists', albums: 'your albums', tracks: 'your tracks' } as const

/** A searchable list of the library's artists, albums or tracks, most played first. */
const Browse = ({ kind }: { kind: 'artists' | 'albums' | 'tracks' }) => {
  const [q, setQ] = useState('')
  const [data, setData] = useState<BrowseResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [nudge, setNudge] = useState(0)
  // the search the list on screen answers; while it lags the box, the list is busy
  const [answered, setAnswered] = useState<string | null>(null)
  const busy = answered !== q
  useEffect(() => {
    let stale = false
    const t = setTimeout(
      () => {
        api
          .libraryBrowse(kind, q.trim(), 200)
          .then((b) => {
            if (stale) return
            setData(b)
            setError(null)
            setAnswered(q)
          })
          .catch((e: unknown) => {
            if (stale) return
            setError(e instanceof Error ? e.message : 'no answer')
            setAnswered(q)
          })
      },
      q ? 250 : 0,
    )
    return () => {
      stale = true
      clearTimeout(t)
    }
  }, [q, kind, nudge])
  const rows: {
    key: string
    title: string
    sub?: string
    image?: string
    count: number
    to: ReturnType<typeof artistTo>
    play: Parameters<typeof PlayButton>[0]['play']
    uri?: string
  }[] = !data
    ? []
    : kind === 'tracks'
      ? (data.tracks ?? []).map((t) => ({
          key: t.id,
          title: t.title,
          sub: String(t.data.artist ?? ''),
          image: t.data.image,
          count: t.count,
          to: itemTo(t.id),
          play: { item: t.id },
          uri: typeof t.data.uri === 'string' ? t.data.uri : undefined,
        }))
      : ((kind === 'artists' ? data.artists : data.albums) ?? []).map((g) => ({
          key: g.key,
          title: g.key,
          sub: kind === 'albums' ? g.artist : `${fmtNum(g.items)} ${g.items === 1 ? 'track' : 'tracks'}`,
          image: g.image,
          count: g.count,
          to: kind === 'artists' ? artistTo(g.key) : albumTo(g.key, g.artist),
          play: kind === 'artists' ? { artist: g.key } : { album: g.key, artist: g.artist },
        }))
  const max = rows[0]?.count ?? 1
  return (
    <div data-testid={`music-${kind}`}>
      <LibraryHeader />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${kind}`}
        type="search"
        aria-label={`Search ${kind}`}
        className="w-full mb-3"
      />
      {error && (
        <Failed
          what={q ? 'the matches' : NOUN[kind]}
          reason={error}
          onRetry={() => setNudge((n) => n + 1)}
          className="mb-3"
        />
      )}
      {!data ? (
        !error && <SkeletonList rows={10} />
      ) : (
        <Busy busy={busy}>
          {rows.length === 0 && <Empty icon="🎧">{q ? 'No matches.' : 'Nothing here yet.'}</Empty>}
          <ul className="group-list">
            {rows.map((r) => (
              <li key={r.key}>
                <Link {...r.to} className="flex items-center gap-3 px-3 py-2 hover:bg-raised/60">
                  <Cover image={r.image} round={kind === 'artists'} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="text-sm truncate">{r.title}</span>
                      <span className="text-xs text-muted tabular-nums shrink-0">{fmtNum(r.count)}×</span>
                    </span>
                    {r.sub && <span className="block text-xs text-faint truncate">{r.sub}</span>}
                    <span className="block h-1 rounded-full bg-line mt-1.5 overflow-hidden">
                      <span
                        className="block h-full bg-accent rounded-full"
                        style={{ width: `${Math.max(2, (r.count / max) * 100)}%` }}
                      />
                    </span>
                  </span>
                  <PlayButton small play={r.play} title={r.title} uri={r.uri} />
                </Link>
              </li>
            ))}
          </ul>
        </Busy>
      )}
    </div>
  )
}

export const MusicArtistsView = () => <Browse kind="artists" />
export const MusicAlbumsView = () => <Browse kind="albums" />
export const MusicTracksView = () => <Browse kind="tracks" />

export const MusicPlaylistsView = () => {
  const playlists = useRead<LibraryItem[]>('playlists', askPlaylists)
  const items = playlists.data
  return (
    <div data-testid="music-playlists">
      <LibraryHeader />
      {!items ? (
        playlists.error ? (
          <Failed what="your playlists" reason={playlists.error} onRetry={playlists.retry} />
        ) : (
          <SkeletonList rows={6} />
        )
      ) : items.length === 0 ? (
        <Empty icon="🎧">
          No playlists yet. Connect Spotify in Settings and they arrive with the next sync.
        </Empty>
      ) : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {items.map((p) => (
            <li key={p.id} className="min-w-0 group">
              <div className="relative aspect-square rounded-[var(--radius-card-inner)] overflow-hidden bg-raised">
                {p.data.image && (
                  <img src={p.data.image} alt="" className="w-full h-full object-cover" loading="lazy" />
                )}
                {typeof p.data.uri === 'string' && (
                  <span className="absolute bottom-2 right-2">
                    <PlayButton play={{ context_uri: p.data.uri }} title={p.title} />
                  </span>
                )}
              </div>
              <div className="text-xs mt-1.5 truncate">{p.title}</div>
              <div className="text-[11px] text-faint truncate">{String(p.data.tracks ?? 0)} tracks</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
